import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Package, Clock, CheckCircle, Truck, XCircle, ArrowRight } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusConfig = {
  pending: { icon: Clock, color: 'bg-yellow-100 text-yellow-800', label: 'Ap tann' },
  processing: { icon: Package, color: 'bg-blue-100 text-blue-800', label: 'Ap prepare' },
  shipped: { icon: Truck, color: 'bg-purple-100 text-purple-800', label: 'Voye' },
  delivered: { icon: CheckCircle, color: 'bg-green-100 text-green-800', label: 'Livre' },
  cancelled: { icon: XCircle, color: 'bg-red-100 text-red-800', label: 'Anile' },
};

export default function OrdersPage() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const { isAuthenticated, getAuthHeader } = useAuth();

  useEffect(() => {
    if (isAuthenticated) {
      fetchOrders();
    }
  }, [isAuthenticated]);

  const fetchOrders = async () => {
    try {
      const response = await axios.get(`${API}/orders`, {
        headers: getAuthHeader()
      });
      setOrders(response.data);
    } catch (error) {
      console.error('Error fetching orders:', error);
    } finally {
      setLoading(false);
    }
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gray-50 py-16" data-testid="orders-page">
        <div className="max-w-2xl mx-auto px-4 text-center">
          <Package className="h-16 w-16 text-mrc-navy mx-auto mb-6" />
          <h1 className="font-heading text-3xl font-bold text-mrc-navy mb-4">
            Kòmand Mwen yo
          </h1>
          <p className="text-muted-foreground mb-8">
            Konekte pou wè kòmand ou yo.
          </p>
          <Link to="/login">
            <Button className="bg-mrc-navy hover:bg-mrc-navy/90 text-white px-8 py-6">
              Konekte
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 py-16">
        <div className="max-w-4xl mx-auto px-4">
          <div className="animate-pulse space-y-6">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="bg-white p-6">
                <div className="bg-gray-200 h-6 w-32 mb-4" />
                <div className="bg-gray-200 h-4 w-48 mb-2" />
                <div className="bg-gray-200 h-4 w-24" />
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" data-testid="orders-page">
      {/* Header */}
      <div className="bg-mrc-navy py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="font-heading text-4xl font-bold text-white">
            Kòmand Mwen yo
          </h1>
          <p className="text-gray-300 mt-2">
            {orders.length} kòmand
          </p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {orders.length === 0 ? (
          <div className="text-center py-16">
            <Package className="h-16 w-16 text-gray-300 mx-auto mb-6" />
            <h2 className="font-heading text-2xl font-bold text-mrc-navy mb-4">
              Pa gen kòmand
            </h2>
            <p className="text-muted-foreground mb-8">
              Ou poko fè okenn kòmand.
            </p>
            <Link to="/products">
              <Button className="bg-mrc-navy hover:bg-mrc-navy/90 text-white px-8">
                Kòmanse Achte
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </div>
        ) : (
          <div className="space-y-6">
            {orders.map((order) => {
              const statusInfo = statusConfig[order.status] || statusConfig.pending;
              const StatusIcon = statusInfo.icon;
              
              return (
                <div 
                  key={order.id} 
                  className="bg-white p-6 shadow-sm"
                  data-testid={`order-${order.id}`}
                >
                  {/* Order Header */}
                  <div className="flex flex-wrap items-start justify-between gap-4 mb-4 pb-4 border-b">
                    <div>
                      <p className="text-sm text-muted-foreground mb-1">Nimewo Kòmand</p>
                      <p className="font-mono font-bold text-mrc-navy">{order.id.slice(0, 8)}...</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground mb-1">Dat</p>
                      <p className="font-medium">
                        {new Date(order.created_at).toLocaleDateString('fr-HT', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric'
                        })}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground mb-1">Total</p>
                      <p className="price font-bold text-mrc-navy">${order.total.toFixed(2)}</p>
                    </div>
                    <div>
                      <Badge className={`${statusInfo.color} flex items-center gap-1`}>
                        <StatusIcon className="h-3 w-3" />
                        {statusInfo.label}
                      </Badge>
                    </div>
                  </div>

                  {/* Order Items */}
                  <div className="space-y-3">
                    {order.items.map((item, index) => (
                      <div key={index} className="flex items-center gap-4">
                        <div className="flex-1">
                          <p className="font-medium">{item.name}</p>
                          <p className="text-sm text-muted-foreground">
                            Kantite: {item.quantity} × ${item.price.toFixed(2)}
                          </p>
                        </div>
                        <p className="price font-bold">${item.subtotal.toFixed(2)}</p>
                      </div>
                    ))}
                  </div>

                  {/* Payment Status */}
                  <div className="mt-4 pt-4 border-t flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">
                      Estati Peman: {' '}
                      <span className={order.payment_status === 'paid' ? 'text-green-600 font-medium' : 'text-yellow-600 font-medium'}>
                        {order.payment_status === 'paid' ? 'Peye' : 'Ap tann'}
                      </span>
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
